// Analytics service for fetching LOI and IRC data
import { api } from './api';

export interface LOIAnalytics {
  mean: number;
  median: number;
  min: number;
  max: number;
  count: number;
  unit: string;
}

export interface IRCAnalytics {
  mean: number;
  median: number;
  completionRate: number;
  incidenceRate: number;
  startedCount: number;
  completedCount: number;
}

export interface ProjectStats {
  total: number;
  completed: number;
  terminated: number;
  quotaFull: number;
  securityTerminated: number;
  qualityTerminated: number;
  invited: number;
}

export interface CompletesProgressPoint {
  date: string;       // YYYY-MM-DD
  completes: number;  // cumulative completes up to and including this day
}

export interface CompletesProgress {
  target: number;                    // sum of collection targets (0 if none set)
  totalCompletes: number;
  series: CompletesProgressPoint[];
}

export interface ProjectAnalytics {
  loiAnalytics: LOIAnalytics;
  ircAnalytics: IRCAnalytics;
  projectStats: ProjectStats;
  completesProgress?: CompletesProgress;
}

class AnalyticsService {
  async getProjectAnalytics(projectId: string): Promise<ProjectAnalytics & { projectId: string; calculatedAt: string }> {
    const response = await api.get(`/projects/${projectId}/analytics`);
    
    console.log(`📨 Analytics API response:`, response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch project analytics');
    }
    
    return response.data.data;
  }

  // Format LOI seconds into human readable format
  formatLOI(seconds: number): string {
    // Handle invalid values
    if (isNaN(seconds) || !isFinite(seconds) || seconds < 0) {
      return '--:--';
    }
    
    if (seconds === 0) {
      return '< 1s';
    }
    
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  }

  // Get LOI status based on typical survey benchmarks
  getLOIStatus(meanSeconds: number): { status: 'good' | 'average' | 'poor'; color: string; description: string } {
    // Handle invalid values
    if (isNaN(meanSeconds) || !isFinite(meanSeconds) || meanSeconds <= 0) {
      return {
        status: 'poor',
        color: 'text-gray-600 bg-gray-50',
        description: 'No data available'
      };
    }
    
    if (meanSeconds < 120) { // Less than 2 minutes
      return {
        status: 'poor',
        color: 'text-red-600 bg-red-50',
        description: 'Very short - possible quality issues'
      };
    } else if (meanSeconds < 300) { // 2-5 minutes
      return {
        status: 'average',
        color: 'text-yellow-600 bg-yellow-50',
        description: 'Short but acceptable'
      };
    } else if (meanSeconds < 900) { // 5-15 minutes
      return {
        status: 'good',
        color: 'text-green-600 bg-green-50',
        description: 'Good length for detailed surveys'
      };
    } else { // Over 15 minutes
      return {
        status: 'average',
        color: 'text-yellow-600 bg-yellow-50',
        description: 'Long - risk of respondent fatigue'
      };
    }
  }

  // Get IRC status based on industry benchmarks
  getIRCStatus(incidenceRate: number): { status: 'excellent' | 'good' | 'average' | 'poor'; color: string; description: string } {
    if (incidenceRate >= 80) {
      return {
        status: 'excellent',
        color: 'text-green-600 bg-green-50',
        description: 'Excellent completion rate'
      };
    } else if (incidenceRate >= 60) {
      return {
        status: 'good',
        color: 'text-blue-600 bg-blue-50',
        description: 'Good completion rate'
      };
    } else if (incidenceRate >= 40) {
      return {
        status: 'average',
        color: 'text-yellow-600 bg-yellow-50',
        description: 'Average completion rate'
      };
    } else {
      return {
        status: 'poor',
        color: 'text-red-600 bg-red-50',
        description: 'Low completion rate - investigate'
      };
    }
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;